import React, { useEffect, useRef, useState } from 'react';

import { Container, Row, Button, Form } from 'react-bootstrap';
import * as Yup from 'yup';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalEditor, CLEAR_EDITOR_COMMAND, $getRoot } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';

import ImageListClipboard from '../ImageListClipboard';
import ToolBarEdit from '../ToolBarEdit';

import api from '../../services/apiClient';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { useImageClipboard } from '../../hooks/imageClipboard';

import './sendMessage.css';
import './editorStyle.css';

const Placeholder: React.FC = () => {
  return <div className="editor-placeholder">Digite sua mensagem</div>;
};

const OnChangePlugin = ({
  onChange,
  setEditMensage,
  setEditIsEmpty,
}: any): JSX.Element => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      onChange(editorState);
      editorState.read(() => {
        let htmlString = '';
        htmlString = $generateHtmlFromNodes(editor);
        setEditMensage(htmlString);

        const root = $getRoot();
        if (root === null) {
          return;
        }
        const firstChild = root.getFirstChild();
        if (firstChild === null) {
          // handle the case where the first child is null
          return;
        }
        const isEmpty = firstChild.isEmpty() && root.getChildrenSize() === 1;

        setEditIsEmpty(isEmpty);
      });
    });
  }, [editor, onChange]);
  return <div> </div>;
};

const ClearEditorButton = (): JSX.Element => {
  const [editor] = useLexicalComposerContext();
  const handleClick = (e: React.MouseEvent): void => {
    editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
  };

  return (
    <Button
      id="btn-clear-editor"
      type="button"
      style={{ display: 'none' }}
      onClick={handleClick}
    >
      Limpar Editor
    </Button>
  );
};

const SendMessage: React.FC<any> = ({ getMessages }) => {
  const { token, user, hubConnection, nameClient } = useAuth().dataConnection;
  const { idRequestSupport } = useAuth();
  const { addToast } = useToast();
  const { imageFile, removeImage, addImageClipboard } = useImageClipboard();

  const bntSendMessage = useRef<HTMLButtonElement>(null);
  const bntClearEditor = useRef<HTMLButtonElement | null>(null);
  const inputFile = useRef<HTMLInputElement>(null);

  const [editorState, setEditorState] = useState<LexicalEditor>(
    {} as LexicalEditor
  );

  const [editMensage, setEditMensage] = useState('');
  const [editIsEmpty, setEditIsEmpty] = useState(true);

  const [typeBold, setTypeBold] = useState(false);
  const [typeItalic, setTypeItalic] = useState(false);
  const [typeUnderline, setTypeUnderline] = useState(false);

  const [showLoadingFile, setShowLoadingFile] = useState(false);

  const disableButtonSend = (): void => {
    if (bntSendMessage.current !== null) {
      bntSendMessage.current.disabled = editIsEmpty;
    }
  };

  useEffect(() => {
    // Adiciona o evento Past, para tratar imagens que estão na memória do Clipboard.
    document.addEventListener('paste', addImageClipboard);

    // Função para desativar o autocomplete
    function disableAutoComplete(): void {
      const inputElements = document.querySelectorAll('input');
      inputElements.forEach((input) => {
        input.setAttribute('autocomplete', 'off');
      });
    }

    // Chame a função ao carregar a página
    disableAutoComplete();
  }, []);

  useEffect(() => {
    disableButtonSend();
  }, [editIsEmpty, imageFile]);

  useEffect(() => {
    const buttonElement = document.getElementById(
      'btn-clear-editor'
    ) as HTMLButtonElement | null;

    bntClearEditor.current = buttonElement;
  }, [ClearEditorButton]);

  const handleSendImage = (): void => {
    const formData = new FormData();
    formData.append('seqAtendimento', String(idRequestSupport));

    for (let i = 0; i < imageFile.length; i += 1) {
      formData.append(`file[${i}]`, imageFile[i].File);
    }

    formData.append('nameClient', String(user));
    formData.append(
      'idConnectionClient',
      hubConnection?.connectionId == null ? '' : hubConnection.connectionId
    );

    api
      .post(`/api/Imagem`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((request) => {
        const { success, data } = request.data;

        if (success) {
          for (let i = 0; i < imageFile.length; i += 1) {
            removeImage(imageFile[i].key);
          }

          getMessages(data);

          addToast({
            type: 'success',
            title: 'Imagem enviada com sucesso',
            description: `O arquivo foi enviado com sucesso!`,
          });
        }
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Falha ao enviar arquivo',
        });
      });
  };

  const removerUltimoParagrafoComQuebraDeLinha = (
    htmlString: string
  ): string => {
    const regex = /<p[^>]*>(\s*<br\s*\/?>\s*)*<\/p>\s*$/;
    return htmlString.replace(regex, '');
  };

  const removeQuebraDeLinha = (str: string): string => {
    return str;
  };

  const removeTextoVazio = (text: string): string => {
    const regex =
      /(<p\s+class="editor-paragraph"[^>]*\s+dir="ltr"[^>]*>\s*<p\s+class="editor-paragraph"[^>]*>(\s*<br\s*\/?>)*\s*<\/p>\s*<\/p>)|(<p\s+class="editor-paragraph"[^>]*\s+dir="ltr"[^>]*>\s*<\/p>)/gi;

    return text.replace(regex, '');
  };

  const convertUrlsToLinks = (text: string): string => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(
      urlRegex,
      (url) =>
        `<a href="${url
          .replace('</p>', '')
          .replace('</span>', '')}" target="_blank">${url.replace(
          '</p>',
          ''
        )}</a>`
    );
  };

  const submitMessage = async (message: string): Promise<void> => {
    try {
      // Remove quebra de linha no final da mensagem ao precisar enter para enviar a mensagem
      let filteredMessage = message.replace(
        '<p class="editor-paragraph" dir="ltr"><br></p>',
        ''
      );

      const regexQuebraDeLinhas =
        /<p\s+class\s*=\s*"editor-paragraph"\s*>\s*((?:<br\s*\/?>\s*)+)<\/p>/gi;
      filteredMessage = filteredMessage.replace(regexQuebraDeLinhas, '');
      filteredMessage = convertUrlsToLinks(filteredMessage);

      if (imageFile.length > 0) handleSendImage();

      if (filteredMessage.length === 0) {
        if (bntClearEditor.current !== null) bntClearEditor.current.click();
        return;
      }

      const schema = Yup.object().shape({
        filteredMessage: Yup.string()
          .required()
          .min(1, 'É necessário informar uma mensagem.')
          .max(
            4000,
            'A menssagem não pode possuir mais do que 4.000 caracteres.'
          ),
      });

      const objectToValidation = {
        filteredMessage,
      };

      await schema.validate(objectToValidation, { abortEarly: false });

      const dataRequest = {
        idSupportRequest: idRequestSupport,
        text: filteredMessage,
        idUser: null,
        nameClient: user,
        idConnectionClient: hubConnection?.connectionId,
      };

      const resultSendMessage = await api.post(
        'api/AtendimentoMensagem',
        dataRequest,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const { success, data } = resultSendMessage.data;

      if (success) {
        if (bntClearEditor.current !== null) {
          bntClearEditor.current.click();
        }
        getMessages(data);
      }

      if (bntSendMessage.current !== null) {
        bntSendMessage.current.disabled = false;
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        addToast({
          type: 'error',
          title: 'Mensagem inválida',
          description: err.message,
        });
      }
    }
  };

  const handleSendFile = (): void => {
    if (inputFile.current === null || inputFile.current === undefined) return;
    if (
      inputFile.current.files === null ||
      inputFile.current.files === undefined
    )
      return;

    const file = inputFile.current?.files[0];

    if (file) {
      setShowLoadingFile(true);

      const formData = new FormData();
      formData.append(
        'seqAtendimento',
        idRequestSupport == null ? '' : idRequestSupport
      );
      formData.append('file', file);
      formData.append('nameClient', nameClient);
      formData.append(
        'idConnectionClient',
        hubConnection?.connectionId == null ? '' : hubConnection.connectionId
      );

      api
        .post(`/api/ArquivoAnexo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((request) => {
          const { success } = request.data;

          if (success) {
            addToast({
              type: 'success',
              title: 'Arquivo enviado com sucesso',
              description: `O arquivo ${file.name} foi enviado com sucesso!`,
            });
          }
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Falha ao enviar arquivo',
          });
        })
        .finally(() => {
          if (inputFile?.current !== null) inputFile.current.value = '';
          setShowLoadingFile(false);
        });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && e.shiftKey === false && e.ctrlKey === false) {
      bntSendMessage.current?.click();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    disableButtonSend();
  };

  const theme = {
    ltr: 'ltr',
    rtl: 'rtl',
    placeholder: 'editor-placeholder',
    paragraph: 'editor-paragraph',
    quote: 'editor-quote',
    heading: {
      h1: 'editor-heading-h1',
      h2: 'editor-heading-h2',
      h3: 'editor-heading-h3',
      h4: 'editor-heading-h4',
      h5: 'editor-heading-h5',
    },
    list: {
      nested: {
        listitem: 'editor-nested-listitem',
      },
      ol: 'editor-list-ol',
      ul: 'editor-list-ul',
      listitem: 'editor-listitem',
    },
    image: 'editor-image',
    link: 'editor-link',
    text: {
      bold: 'editor-text-bold',
      italic: 'editor-text-italic',
      overflowed: 'editor-text-overflowed',
      hashtag: 'editor-text-hashtag',
      underline: 'editor-text-underline',
      strikethrough: 'editor-text-strikethrough',
      underlineStrikethrough: 'editor-text-underlineStrikethrough',
      code: 'editor-text-code',
    },
    code: 'editor-code',
    codeHighlight: {
      atrule: 'editor-tokenAttr',
      attr: 'editor-tokenAttr',
      boolean: 'editor-tokenProperty',
      builtin: 'editor-tokenSelector',
      cdata: 'editor-tokenComment',
      char: 'editor-tokenSelector',
      class: 'editor-tokenFunction',
      'class-name': 'editor-tokenFunction',
      comment: 'editor-tokenComment',
      constant: 'editor-tokenProperty',
      deleted: 'editor-tokenProperty',
      doctype: 'editor-tokenComment',
      entity: 'editor-tokenOperator',
      function: 'editor-tokenFunction',
      important: 'editor-tokenVariable',
      inserted: 'editor-tokenSelector',
      keyword: 'editor-tokenAttr',
      namespace: 'editor-tokenVariable',
      number: 'editor-tokenProperty',
      operator: 'editor-tokenOperator',
      prolog: 'editor-tokenComment',
      property: 'editor-tokenProperty',
      punctuation: 'editor-tokenPunctuation',
      regex: 'editor-tokenVariable',
      selector: 'editor-tokenSelector',
      string: 'editor-tokenSelector',
      symbol: 'editor-tokenProperty',
      tag: 'editor-tokenProperty',
      url: 'editor-tokenOperator',
      variable: 'editor-tokenVariable',
    },
  };

  const editorConfig = {
    namespace: 'test-editor',
    theme,
    onError(error: any) {
      throw error;
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
    ],
  };

  const onChange = (editorTemp: LexicalEditor): void => {
    setEditorState(editorTemp);
  };

  return (
    <Container className="mt-1" style={{ width: '100%', maxWidth: '84%' }}>
      <Row
        className="justify-content-md-center ms-1 me-1"
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <div className="d-flex overflow-auto container">
          <div className="border-0">
            {imageFile.length > 0 && (
              <ImageListClipboard imageFile={imageFile} />
            )}
          </div>
        </div>
        <div className="d-flex">
          <div style={{ width: '100%' }}>
            <LexicalComposer initialConfig={editorConfig}>
              <div className="editor-container">
                <div className="editor-inner">
                  <ToolBarEdit
                    typeBold={typeBold}
                    typeItalic={typeItalic}
                    typeUnderline={typeUnderline}
                    setTypeBold={setTypeBold}
                    setTypeItalic={setTypeItalic}
                    setTypeUnderline={setTypeUnderline}
                    inputFile={inputFile}
                  />
                  <ClearEditorButton />
                  <RichTextPlugin
                    contentEditable={
                      <ContentEditable className="editor-input" />
                    }
                    placeholder={<Placeholder />}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                  <ClearEditorPlugin />
                  <HistoryPlugin />
                  <AutoFocusPlugin />
                  <ListPlugin />
                  <LinkPlugin />
                  <OnChangePlugin
                    onChange={onChange}
                    setEditMensage={setEditMensage}
                    setEditIsEmpty={setEditIsEmpty}
                  />
                </div>
              </div>
            </LexicalComposer>
          </div>
          <div className="buttonSendMessage">
            <Button
              id="btnSendMessage"
              ref={bntSendMessage}
              size="sm"
              variant="primary"
              type="submit"
              disabled={false}
              onClick={() => {
                submitMessage(editMensage);
              }}
            >
              <img
                className="img-send-message"
                src="./assets/send-message.svg"
                alt="Enviar Mensagem"
              />
            </Button>
          </div>

          <Form.Control
            type="file"
            ref={inputFile}
            className="visually-hidden"
            onChange={() => {
              handleSendFile();
            }}
          />
        </div>
      </Row>
    </Container>
  );
};

export default SendMessage;
